import Styles from './styles.module.css';
import React, { useState } from 'react';
import ErrorMessage from '../../../../package/error/index';
import DefaultTitle from '../../../../package/title/index';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { pamphletData, pamphletUrl, ValidationError } from 'src/type';
import DefaultSelect1 from 'src/salon/package/1-select';
import DefaultDesc from 'src/salon/package/desc';

type Props = {
  documentId: string;
  setChangeData: React.Dispatch<React.SetStateAction<boolean>>;
  pamphlet: pamphletData;
  setPamphlet: React.Dispatch<React.SetStateAction<pamphletData>>;
};

const JobPostingsPamphlet: React.FC<Props> = ({ documentId, setChangeData, pamphlet }) => {

  const db = getFirestore();

  const [ errorMessage, setErrorMessage ] = useState<ValidationError>(null);
  const [ banner, setBanner ] = useState(false);

  const [ file, setFile ] = useState<File | null>(null);
  const [ pamphletExist, setPamphletExist ] = useState<boolean | null>(
    pamphlet.exist
  );

  /* =============================
   * File Handler
   * ============================= */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setChangeData(true);
  };

  const hasUploadedPamphlet = pamphlet.id !== '';
  const hasNewFile = file instanceof File;

  /* =============================
   * Validation
   * ============================= */
  const validate = (): ValidationError => {
    if (pamphletExist === null) {
      return {
        message: 'パンフレットの有無を選択してください。',
        fieldId: 'pamphlet.exist',
      };
    }

    if (pamphletExist && !hasUploadedPamphlet && !hasNewFile) {
      return {
        message: 'パンフレットを添付してください。',
        fieldId: 'pamphlet.input',
      };
    }

    return null;
  };

  const handleSubmit = () => {
    window.scrollTo(0, 0);
    const err = validate();
    setErrorMessage(err);
    if (err) return;

    save(true);
  };

  /* =============================
   * Upload
   * ============================= */
  const uploadPamphlet = async () => {
    if (!hasNewFile || !file) return;

    const storage = getStorage();
    const storageRef = ref(storage, `pamphlet/${documentId}`);
    // 同じ名前で上書きするため、ブラウザ等に古いPDFを覚えさせない(既定では1時間使い回される)
    await uploadBytes(storageRef, file, { cacheControl: 'no-cache' });
  };

  /* =============================
   * Firestore Update
   * ============================= */
  const updatePamphlet = async (isCompleted: boolean) => {
    const base = {
      isCompleted,
      time: new Date(),
    };

    if (!hasNewFile && hasUploadedPamphlet) {
      // 既存ファイルそのまま
      await updateDoc(doc(db, 'recruits', documentId), {
        'pamphlet.exist': pamphletExist,
        'pamphlet.isCompleted': isCompleted,
        'pamphlet.time': new Date(),
      });
      return;
    }

    await updateDoc(doc(db, 'recruits', documentId), {
      pamphlet: {
        ...base,
        exist: pamphletExist,
        id: hasNewFile ? documentId : '',
      },
    });
  };

  /* =============================
   * Save
   * ============================= */

  const [ uploading, setUploading ] = useState(false);

  const save = async (isCompleted: boolean) => {
    setUploading(true);
    
    try {
      await uploadPamphlet();
      await updatePamphlet(isCompleted);
      
      setChangeData(false);
      setFile(null);
    } catch (e) {
      console.error('保存失敗', e);
      setUploading(false);
    } finally {
      setUploading(false);
      setBanner(true);
      setTimeout(() => setBanner(false), 1500);
    }
  };

  /* =============================
   * UI
   * ============================= */
  return (
    <>
      {errorMessage && <ErrorMessage message={errorMessage.message} />}
      {banner && <div className={Styles.banner}>保存しました。</div>}
      {uploading && (
        <div className={Styles.lockOverlay}>
          <div className={Styles.spinner} />
          <p>アップロード中...</p>
        </div>
      )}

      <div className={Styles.title_edit}>
        <h4>パンフレットの編集</h4>
        <div className={Styles.title_edit_btn}>
          <button className={Styles.save_box_now} onClick={() => save(false)}>一時保存</button>
          <button className={Styles.save_box_save} onClick={handleSubmit}>保存</button>
        </div>
      </div>

      <div className={Styles.test_box}>
        <DefaultSelect1
          error={errorMessage?.fieldId === 'pamphlet.exist'}
          title="パンフレット有無"
          list={['未選択', 'パンフレット有り', 'パンフレット無し']}
          value={ pamphletExist === null ? '未選択' : pamphletExist ? 'パンフレット有り' : 'パンフレット無し' }
          setValue={(val: string) => {
            setPamphletExist( val === 'パンフレット有り' ? true : val === 'パンフレット無し' ? false : null );
          }}
        />

        {pamphletExist && (
          <>
            <DefaultTitle title="パンフレット添付" />

            <div className={Styles.pamphlet}>
              <label style={ errorMessage?.fieldId === 'pamphlet.input' ? { border: '1px solid red', color: 'red' } : {} }>
                {file !== null ? "更新済み" : hasUploadedPamphlet || hasNewFile ? '書類 添付済み' : '書類 未添付'}
                <input type="file" onChange={handleFileChange} />
              </label>

              {hasUploadedPamphlet && (
                <a className={Styles.pamphlet_loading}
                  href={pamphletUrl(documentId, pamphlet.version)} target="_blank" rel="noreferrer" >
                  プレビュー
                </a>
              )}
            </div>

            {!hasUploadedPamphlet && !hasNewFile && (
              <DefaultDesc text={"※パンフレットのPDFデータを添付してください"} type={"warning"} error={true} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default JobPostingsPamphlet;
